import { Tabs, TabsList, TabsTrigger } from '@/components/Tabs/Tabs';
import { type OrderQueueParams } from '@/features/reports/api';
import { BrandsPanel } from '@/features/reports/brands/BrandsPanel';
import { AppUsagePanel } from '@/features/reports/app/AppUsagePanel';
import { ClientsPanel } from '@/features/reports/clients/ClientsPanel';
import { REPORTS_COPY as COPY, TRANSACTIONS_COPY } from '@/features/reports/copy';
import { HeatmapPanel } from '@/features/reports/heatmap/HeatmapPanel';
import { OverviewTab } from '@/features/reports/overview/OverviewTab';
import { REPORT_PERIOD, type ReportPeriod } from '@/features/reports/periods';
import {
    type AppUsageDays,
    CLIENTS_VIEW,
    CLIENTS_VIEWS,
    DEFAULT_APP_USAGE_DAYS,
    DEFAULT_HEATMAP_WEEKS,
    type ClientsView,
    type HeatmapWeeks,
    REPORT_TAB,
    REPORT_TABS,
    type ReportTab,
    TRANSACTIONS_VIEW,
    TRANSACTIONS_VIEWS,
    type TransactionsView,
} from '@/features/reports/tabs';
import { TransactionsPanel } from '@/features/reports/transactions/TransactionsPanel';
import { Page, PageBody } from '@/layouts/Page';
import { PageHeader } from '@/layouts/PageHeader';

/** Everything Reports keeps in the address, so a report can be shared and survives a reload. */
export interface ReportsSearch {
    tab?: ReportTab;
    period?: ReportPeriod;
    view?: TransactionsView | ClientsView;
    /** The page's relationship manager: the order queue's RM filter and Clients' scope alike. */
    rmId?: number;
    /** Clients' inactive window; absent is Reports:InactiveAfterDays. */
    inactiveDays?: number;
    /** Brand intelligence's brand, which is not the order queue's brand filter. */
    brand?: number;
    /** Brand intelligence's quarter, "2026-Q3"; absent is the current one. */
    quarter?: string;
    /** App usage's window; absent is 30 days. */
    days?: AppUsageDays;
    /** The Client heatmap's window; absent is 6 weeks. */
    weeks?: HeatmapWeeks;
    /** Lookbook page attention's lookbook; absent is the most viewed in the window. */
    lookbookId?: number;
    queue: Omit<OrderQueueParams, 'rmId'>;
}

const isTransactionsView = (view: string | undefined): view is TransactionsView =>
    (TRANSACTIONS_VIEWS as readonly string[]).includes(view ?? '');

const isClientsView = (view: string | undefined): view is ClientsView =>
    (CLIENTS_VIEWS as readonly string[]).includes(view ?? '');

/**
 * Reports (ALS-85, ALS-86, ALS-87, ALS-88, ALS-89, ALS-91, ALS-160): the page and its tabs. Brand
 * intelligence ignores the relationship manager: it reports a brand across the whole clientele.
 * The period and the relationship manager are shared, so moving between tabs keeps the window and
 * the clients the reader chose. A view that belongs to the other tab falls back to this tab's
 * first.
 */
export function ReportsPage({
    search,
    onChange,
}: {
    search: ReportsSearch;
    onChange: (next: ReportsSearch) => void;
}) {
    const tab = search.tab ?? REPORT_TAB.Overview;
    const period = search.period ?? REPORT_PERIOD.Month;

    return (
        <Page surface="main">
            <PageHeader eyebrow={COPY.eyebrow} title={COPY.title} />
            <PageBody>
                <Tabs
                    value={tab}
                    onValueChange={(next) => onChange({ ...search, tab: next as ReportTab })}
                >
                    {/* Five tabs do not fit a 320 phone: the strip scrolls inside itself, fades
                        the side with more past it and keeps the chosen tab in sight (ALS-160). Each
                        tab is sized to its label, as the sub-tab strip is: shared out equally,
                        "Transactions" lost its padding and touched the pill's edges (ALS-479). */}
                    <TabsList scrollable aria-label={COPY.title} className="self-start">
                        {REPORT_TABS.map((t) => (
                            <TabsTrigger key={t} value={t} className="flex-none">
                                {TRANSACTIONS_COPY.tabs[t]}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>
                {tab === REPORT_TAB.Transactions ? (
                    <TransactionsPanel
                        period={period}
                        view={
                            isTransactionsView(search.view) ? search.view : TRANSACTIONS_VIEW.Orders
                        }
                        queue={{ ...search.queue, rmId: search.rmId }}
                        onPeriodChange={(next) => onChange({ ...search, period: next })}
                        onViewChange={(next) => onChange({ ...search, view: next })}
                        onQueueChange={({ rmId, ...queue }) => onChange({ ...search, queue, rmId })}
                    />
                ) : tab === REPORT_TAB.Brands ? (
                    <BrandsPanel
                        brandId={search.brand}
                        quarter={search.quarter}
                        onBrandChange={(brand) => onChange({ ...search, brand })}
                        onQuarterChange={(quarter) => onChange({ ...search, quarter })}
                    />
                ) : tab === REPORT_TAB.Clients ? (
                    <ClientsPanel
                        view={isClientsView(search.view) ? search.view : CLIENTS_VIEW.Spend}
                        params={{ period, inactiveDays: search.inactiveDays, rmId: search.rmId }}
                        onViewChange={(next) => onChange({ ...search, view: next })}
                        onPeriodChange={(next) => onChange({ ...search, period: next })}
                        onManagerChange={(rmId) => onChange({ ...search, rmId })}
                        onWindowChange={(inactiveDays) => onChange({ ...search, inactiveDays })}
                    />
                ) : tab === REPORT_TAB.Heatmap ? (
                    <HeatmapPanel
                        params={{ weeks: search.weeks ?? DEFAULT_HEATMAP_WEEKS, rmId: search.rmId }}
                        lookbookId={search.lookbookId}
                        onWeeksChange={(weeks) => onChange({ ...search, weeks })}
                        onManagerChange={(rmId) => onChange({ ...search, rmId })}
                        onLookbookChange={(lookbookId) => onChange({ ...search, lookbookId })}
                    />
                ) : tab === REPORT_TAB.AppUsage ? (
                    <AppUsagePanel
                        params={{ days: search.days ?? DEFAULT_APP_USAGE_DAYS, rmId: search.rmId }}
                        onDaysChange={(days) => onChange({ ...search, days })}
                        onManagerChange={(rmId) => onChange({ ...search, rmId })}
                    />
                ) : (
                    <OverviewTab
                        period={period}
                        onPeriodChange={(next) => onChange({ ...search, period: next })}
                    />
                )}
            </PageBody>
        </Page>
    );
}
